import React, { useState, useMemo } from 'react';
import { TIPOS, SUB_LABELS, fmt, fmtDate } from './constants';
import MovimientoForm from './MovimientoForm';

export default function Movimientos({ movimientos, onSave, onDelete }) {
  const [showForm, setShowForm]     = useState(false);
  const [editId,   setEditId]       = useState(null);
  const [delConfirm, setDelConfirm] = useState(null);
  const [filtro, setFiltro]         = useState({tipo:'todos',mes:'',buscar:''});
  const [page, setPage]             = useState(1);
  const PER_PAGE = 50;

  const meses = useMemo(()=>
    [...new Set(movimientos.map(m=>m.fecha.slice(0,7)))].sort().reverse()
  ,[movimientos]);

  const filtered = useMemo(()=>movimientos.filter(m=>{
    if (filtro.tipo!=='todos' && m.tipo!==filtro.tipo) return false;
    if (filtro.mes && !m.fecha.startsWith(filtro.mes)) return false;
    if (filtro.buscar){
      const q=filtro.buscar.toLowerCase();
      if (![m.descripcion,m.categoria,m.razonSocial,m.nFactura,m.pn,m.notas].join(' ').toLowerCase().includes(q)) return false;
    }
    return true;
  }).sort((a,b)=>b.fecha.localeCompare(a.fecha)),[movimientos,filtro]);

  const paged = useMemo(()=>filtered.slice((page-1)*PER_PAGE, page*PER_PAGE),[filtered,page]);
  const totalPages = Math.ceil(filtered.length/PER_PAGE);

  const handleSave = (data)=>{ onSave(data,editId); setShowForm(false); setEditId(null); };
  const handleEdit = (m)=>{ setEditId(m.id); setShowForm(true); window.scrollTo({top:0,behavior:'smooth'}); };
  const editFormData = editId ? (()=>{ const m=movimientos.find(x=>x.id===editId); return m?{...m,monto:String(m.monto),monto_iva:String(m.monto_iva||''),monto_total:String(m.monto_total||'')}:null; })() : null;

  const totVenta  = useMemo(()=>filtered.filter(m=>m.tipo==='venta').reduce((a,m)=>a+m.monto,0),[filtered]);
  const totCompra = useMemo(()=>filtered.filter(m=>m.tipo!=='venta').reduce((a,m)=>a+m.monto,0),[filtered]);

  return (
    <div>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:14}}>
        <h2 style={{fontSize:15,fontWeight:700}}>
          <i className="ti ti-list" style={{marginRight:7,color:'var(--blue)'}} />Movimientos
        </h2>
        {!showForm && (
          <button className="primary" onClick={()=>{setEditId(null);setShowForm(true);}}>
            <i className="ti ti-plus" />Nuevo
          </button>
        )}
      </div>

      {showForm && <MovimientoForm editData={editFormData} onSave={handleSave} onCancel={()=>{setShowForm(false);setEditId(null);}} />}

      {/* Filtros */}
      <div style={{display:'flex',gap:8,flexWrap:'wrap',marginBottom:12,alignItems:'center'}}>
        <select value={filtro.tipo} onChange={e=>{setFiltro(f=>({...f,tipo:e.target.value}));setPage(1);}} style={{width:'auto',minWidth:140}}>
          <option value="todos">Todos los tipos</option>
          {Object.entries(TIPOS).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}
        </select>
        <select value={filtro.mes} onChange={e=>{setFiltro(f=>({...f,mes:e.target.value}));setPage(1);}} style={{width:'auto',minWidth:120}}>
          <option value="">Todos los meses</option>
          {meses.map(m=><option key={m} value={m}>{m}</option>)}
        </select>
        <input type="text" placeholder="Buscar descripción, RUT, P/N..." value={filtro.buscar}
          onChange={e=>{setFiltro(f=>({...f,buscar:e.target.value}));setPage(1);}}
          style={{minWidth:200,flex:1}} />
        <span style={{fontSize:12,color:'var(--text3)',whiteSpace:'nowrap'}}>{filtered.length} registros</span>
      </div>

      {/* Subtotales */}
      {filtered.length>0 && (
        <div style={{display:'flex',gap:12,marginBottom:12,flexWrap:'wrap'}}>
          <span style={{fontSize:12,color:'var(--green)',background:'var(--green-bg)',padding:'4px 10px',borderRadius:99,border:'1px solid var(--green-border)'}}>
            Ventas: {fmt(totVenta)}
          </span>
          <span style={{fontSize:12,color:'var(--red)',background:'var(--red-bg)',padding:'4px 10px',borderRadius:99,border:'1px solid var(--red-border)'}}>
            Compras/Gastos: {fmt(totCompra)}
          </span>
        </div>
      )}

      {/* Lista */}
      {paged.length===0
        ? <div style={{textAlign:'center',padding:'3rem',color:'var(--text3)'}}>
            <i className="ti ti-inbox" style={{fontSize:40,display:'block',marginBottom:8,opacity:0.5}} />
            <p style={{fontSize:13}}>{movimientos.length===0?'Sin movimientos. Usa "Cargar datos" para importar.':'Sin resultados.'}</p>
          </div>
        : <div style={{display:'flex',flexDirection:'column',gap:5}}>
            {paged.map(m=>{
              const cfg=TIPOS[m.tipo]||TIPOS.otro;
              const isVenta=m.tipo==='venta';
              const isSF=m.subTipo==='sin_factura';
              return (
                <div key={m.id} style={{
                  background:'var(--card)',border:'1px solid var(--border)',
                  borderLeft:`3px solid ${cfg.color}`,
                  borderRadius:'var(--radius)',padding:'9px 12px',
                  display:'flex',alignItems:'center',gap:10,
                  opacity: isSF ? 0.85 : 1,
                }}>
                  <div style={{width:34,height:34,borderRadius:'var(--radius-sm)',background:cfg.bg,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                    <i className={`ti ${cfg.icon}`} style={{fontSize:16,color:cfg.color}} />
                  </div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{display:'flex',alignItems:'center',gap:6,flexWrap:'wrap'}}>
                      <span style={{fontWeight:600,fontSize:12}}>{m.descripcion||m.categoria}</span>
                      {isSF && <span style={{fontSize:10,background:'rgba(245,158,11,0.15)',color:'var(--amber)',padding:'1px 6px',borderRadius:99,border:'1px solid rgba(245,158,11,0.3)'}}>S/F</span>}
                      {m.nFactura && m.nFactura!=='S/F' && <span style={{fontSize:10,color:'var(--text3)'}}>#{m.nFactura}</span>}
                    </div>
                    <p style={{fontSize:11,color:'var(--text2)',marginTop:2,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                      {m.razonSocial||cfg.label} {m.pn?`· ${m.pn}`:''} · {m.fecha}
                    </p>
                  </div>
                  <div style={{textAlign:'right',flexShrink:0}}>
                    <p style={{fontWeight:700,fontSize:13,color:isVenta?'var(--green)':'var(--red)'}}>
                      {isVenta?'+':'-'}{fmt(m.monto)}
                    </p>
                    {m.monto_iva>0 && <p style={{fontSize:10,color:'var(--text3)'}}>IVA {fmt(m.monto_iva)}</p>}
                  </div>
                  <div style={{display:'flex',gap:3,flexShrink:0}}>
                    <button className="icon-btn" onClick={()=>handleEdit(m)} title="Editar"><i className="ti ti-edit" style={{fontSize:14}} /></button>
                    <button className="icon-btn danger" onClick={()=>setDelConfirm(m.id)} title="Eliminar"><i className="ti ti-trash" style={{fontSize:14}} /></button>
                  </div>
                </div>
              );
            })}
          </div>
      }

      {/* Paginación */}
      {totalPages>1 && (
        <div style={{display:'flex',gap:6,justifyContent:'center',marginTop:14,alignItems:'center'}}>
          <button onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={page===1} style={{padding:'5px 12px',fontSize:12}}>‹ Anterior</button>
          <span style={{fontSize:12,color:'var(--text2)'}}>{page} / {totalPages}</span>
          <button onClick={()=>setPage(p=>Math.min(totalPages,p+1))} disabled={page===totalPages} style={{padding:'5px 12px',fontSize:12}}>Siguiente ›</button>
        </div>
      )}

      {/* Modal eliminar */}
      {delConfirm && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.6)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:100}}
          onClick={e=>{if(e.target===e.currentTarget)setDelConfirm(null);}}>
          <div style={{background:'var(--card2)',border:'1px solid var(--border2)',borderRadius:'var(--radius-lg)',padding:'2rem',width:310,textAlign:'center'}}>
            <i className="ti ti-alert-triangle" style={{fontSize:36,color:'var(--red)',display:'block',marginBottom:12}} />
            <p style={{fontWeight:600,marginBottom:8}}>¿Eliminar movimiento?</p>
            <p style={{fontSize:12,color:'var(--text2)',marginBottom:18}}>Esta acción no se puede deshacer.</p>
            <div style={{display:'flex',gap:8,justifyContent:'center'}}>
              <button onClick={()=>setDelConfirm(null)}>Cancelar</button>
              <button className="danger" onClick={()=>{onDelete(delConfirm);setDelConfirm(null);}}>
                <i className="ti ti-trash" />Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
